/** The price a customer actually pays: the sale price when one is active
 *  (the DB guarantees it is always lower than priceCents whenever set),
 *  otherwise the regular price. Every checkout path must charge this, not
 *  priceCents directly, or a sale shown in the UI would not be honored. */
export function effectivePriceCents(product) {
    return product.salePriceCents ?? product.priceCents;
}
/**
 * Tax owed on one order line (t0947).
 *
 * Rounded per unit, then multiplied by quantity — not rounded on the
 * extended total. A serialized cart line of qty N is expanded into N order
 * items of qty 1 by checkout, while the cart preview still sees one line of
 * qty N; `round(unit) * qty` is the only rule that gives the same answer
 * either way, so the number shown in the cart always matches what the order
 * is actually charged.
 */
export function lineTaxCents(unitPriceCents, qty, rateBps, taxExempt) {
    if (taxExempt || !rateBps)
        return 0;
    return Math.round((unitPriceCents * rateBps) / 10_000) * qty;
}
/**
 * Whether one combination entry is true of a device (t1158). `true` means the
 * device carries the attribute as a checked-off fact; a text or number value
 * must equal the device's, ignoring case and surrounding spaces. A missing
 * attribute or an explicit `false` never matches, so "not filtered" can't
 * quietly pick up the "filtered" price.
 */
function attributeEntryMatches(deviceValue, wanted) {
    if (deviceValue === undefined || deviceValue === false)
        return false;
    if (wanted === true)
        return true;
    return String(deviceValue).trim().toLowerCase() === String(wanted).trim().toLowerCase();
}
/**
 * The full price a device sells for because of the attributes it carries, or
 * null when no combination applies (t1158) — the caller then falls back to the
 * model's normal price.
 *
 * A combination applies when EVERY one of its attributes is true of the
 * device. When several apply, the most specific one wins (most attributes —
 * "Waze + Filtered" beats "Waze"); a tie takes the higher price.
 */
export function attributePriceFor(unitAttributes, combinations) {
    const byName = new Map();
    for (const [name, value] of Object.entries(unitAttributes)) {
        byName.set(name.trim().toLowerCase(), value);
    }
    let best = null;
    for (const combo of combinations) {
        const entries = Object.entries(combo.attributes);
        if (entries.length === 0)
            continue;
        const applies = entries.every(([name, wanted]) => attributeEntryMatches(byName.get(name.trim().toLowerCase()), wanted));
        if (!applies)
            continue;
        if (!best ||
            entries.length > best.size ||
            (entries.length === best.size && combo.priceCents > best.priceCents)) {
            best = { size: entries.length, priceCents: combo.priceCents };
        }
    }
    return best ? best.priceCents : null;
}
//# sourceMappingURL=pricing.js.map